// createLogin — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'create-login' })` op (DSE2 §3.F1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice F1 — Logins / Users / Roles UI.
//
// LOGIN is mssql-only (server-level principal). The synthesizer throws on
// every other engine with a documented redirect to `create-user` /
// `create-role`. The consumer surfaces this as a per-engine gate.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, LoginDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface CreateLoginArgs {
  readonly login: LoginDef;
}

export function buildCreateLoginEdit(args: CreateLoginArgs): DdlEdit {
  return { op: 'create-login', login: args.login };
}

export async function commitCreateLogin(
  ctx: VqeDdlEditContext,
  args: CreateLoginArgs,
): Promise<void> {
  const edit = buildCreateLoginEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isCreateLoginSupported(engine: SqlEngine): boolean {
  return engine === 'mssql';
}

export function getCreateLoginUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return 'SQLite has no auth model — connection-level access is OS / file-permission driven.';
  }
  if (engine === 'postgres') {
    return 'PostgreSQL collapses logins into roles. Use "Role" with the LOGIN attribute set.';
  }
  if (engine === 'mysql' || engine === 'oracle') {
    return `${engine} has no server-level LOGIN — use "User" instead.`;
  }
  return '';
}

export function validateCreateLoginForEngine(
  engine: SqlEngine,
  args: CreateLoginArgs,
): string | null {
  if (!isCreateLoginSupported(engine)) {
    return getCreateLoginUnsupportedTooltip(engine);
  }
  const l = args.login;
  if (l.name.trim().length === 0) {
    return 'Login name is required.';
  }
  const hasPassword = !!l.mssqlPassword && l.mssqlPassword.length > 0;
  const hasFromWindows = l.mssqlFromWindows === true;
  if (hasPassword && hasFromWindows) {
    return 'Pick SQL password OR Windows authentication, not both.';
  }
  if (!hasPassword && !hasFromWindows) {
    return 'Logins require either a SQL password or Windows authentication.';
  }
  return null;
}
